import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { tokens } from '../../theme';

// Calendar for picking a range with two clicks (start, then end) with months stacked in a
// scrollable list, like in the Wildberries cabinet. Dates are ISO strings (YYYY-MM-DD) of Moscow
// calendar days; days outside [min, max] are disabled.

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Month cells: leading blanks (Monday first) then 1..N.
const monthCells = (y, m) => {
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  return [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
};

const RangeCalendar = ({ from, to, min, max, onChange }) => {
  // picking: the first click was made and the second one is awaited.
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState(null);
  const scroller = useRef(null);
  const target = useRef(null);

  const months = useMemo(() => {
    const [y0, m0] = min.split('-').map(Number);
    const [y1, m1] = max.split('-').map(Number);
    const out = [];
    for (let i = y0 * 12 + (m0 - 1); i <= y1 * 12 + (m1 - 1); i += 1) out.push({ y: Math.floor(i / 12), m: i % 12 });
    return out;
  }, [min, max]);

  // Open on the month of the start date (or the latest month) once, when the calendar appears:
  // scrolling again on every click made the list jump when a day of another month was picked.
  // The scroller is position: relative, so the month's offsetTop is already relative to it.
  const [focusKey] = useState(() => (from || max).slice(0, 7));
  useEffect(() => {
    if (scroller.current && target.current) scroller.current.scrollTop = target.current.offsetTop;
  }, []);

  const click = (day) => {
    if (!picking) {
      onChange({ from: day, to: day });
      setPicking(true);
    } else {
      const start = from || day;
      onChange(day < start ? { from: day, to: start } : { from: start, to: day });
      setPicking(false);
      setHover(null);
    }
  };

  // While the second click is awaited the range follows the pointer.
  const lo = picking && hover ? (hover < from ? hover : from) : from;
  const hi = picking && hover ? (hover < from ? from : hover) : to;

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 0.5, pb: 0.5 }}>
        {WEEKDAYS.map((w, i) => (
          <Typography key={w} variant="caption" align="center" sx={{ color: i > 4 ? tokens.error : tokens.inkMuted, fontWeight: 600 }}>{w}</Typography>
        ))}
      </Box>
      <Box ref={scroller} tabIndex={0} aria-label="Календарь, прокручивайте месяцы вверх и вниз"
        sx={{ maxHeight: 300, overflowY: 'auto', position: 'relative', pr: 0.5, borderTop: `1px solid ${tokens.border}` }}>
        {months.map(({ y, m }) => {
          const key = `${y}-${pad(m + 1)}`;
          return (
            <Box key={key} ref={key === focusKey ? target : null} sx={{ pt: 1.25, pb: 0.5 }}>
              <Typography variant="subtitle2" align="center" sx={{ mb: 0.75 }}>{MONTHS[m]} {y}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: '2px' }}>
                {monthCells(y, m).map((d, i) => {
                  if (d === null) return <Box key={`b${i}`} />;
                  const day = iso(y, m, d);
                  const disabled = day < min || day > max;
                  const isEdge = day === lo || day === hi;
                  const inside = !disabled && lo && hi && day > lo && day < hi;
                  return (
                    <Box key={day} sx={{ bgcolor: inside ? tokens.primaryTint : 'transparent', display: 'flex', justifyContent: 'center' }}>
                      <Box component="button" type="button" disabled={disabled}
                        onClick={() => click(day)} onMouseEnter={() => picking && setHover(day)}
                        aria-label={day.split('-').reverse().join('.')} aria-pressed={isEdge}
                        sx={{
                          width: 34, height: 34, border: 'none', borderRadius: '50%', cursor: disabled ? 'default' : 'pointer', font: 'inherit', fontSize: '0.875rem',
                          bgcolor: isEdge ? tokens.primary : 'transparent', color: isEdge ? '#fff' : disabled ? tokens.border : tokens.ink, fontWeight: isEdge ? 700 : 400,
                          '&:hover:not(:disabled)': { bgcolor: isEdge ? tokens.primaryDark : tokens.primaryTint },
                        }}>
                        {d}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default RangeCalendar;
